import * as React from "react";
import { useBloomreachPageRoutes } from "@grandvisionhq/bloomreach";
import { useGoogleMapsStaticMap } from "@grandvisionhq/common";
import { useLink } from "@grandvisionhq/frk-routing";
import { Spinner, Alert } from "@grandvisionhq/iris-ui";
import { useIntl, useDispatch } from "@grandvisionhq/state";
import { DateUtils } from "@grandvisionhq/utils";
import { rescheduleAppointment } from "../../../../../../data-layer/appointments/data-layer/appointments/actions.js";
import { useGetAppointmentById } from "../../../../../hooks/Appointments/hooks/useGetAppointmentById.js";
import AppointmentDetail from "./AppointmentDetail.js";
import useVirtualAppointment from "../../../../../hooks/Appointments/hooks/useVirtualAppointment.js";
const AppointmentDetailContainer = ({ appointmentId }) => {
  const { dateTime, getFormattedLabel, getLabel } = useIntl();
  const dispatch = useDispatch();
  const { isVirtualAppointmentType } = useVirtualAppointment();
  const [storeDetailModalOpen, setStoreDetailModalOpen] = React.useState(false);
  const storeButtonRef = React.createRef();
  const { data: appointment, loading, error } = useGetAppointmentById(appointmentId);
  const returnLabel = getLabel("appointments.rescheduling.returnLabel");
  const { routes } = useBloomreachPageRoutes();
  const { href: appointmentsRoute } = useLink({ route: "me.appointments" }) || {};
  const { href: appointmentsCancelRoute } = useLink({
    route: "appointments.cancel",
    params: {
      id: appointmentId,
      returnPath: appointmentsRoute,
      returnLabel
    }
  }) || {};
  const { href: appointmentsRescheduleRoute } = useLink({
    route: "appointments.reschedule",
    params: {
      id: appointmentId,
      returnPath: appointmentsRoute,
      returnLabel
    }
  }) || {};
  const rescheduleAppointmentRouteFromBloomreach = routes["appointment-reschedule"];
  const cancelAppointmentRouteFromBloomreach = routes["appointment-cancel"];
  const reschedulePath = appointmentsRescheduleRoute ?? (rescheduleAppointmentRouteFromBloomreach ? `${rescheduleAppointmentRouteFromBloomreach}?id=${encodeURIComponent(
    appointmentId
  )}&returnPath=${appointmentsRoute}&returnLabel=${encodeURIComponent(returnLabel)}` : void 0);
  const cancelPath = appointmentsCancelRoute ?? (cancelAppointmentRouteFromBloomreach ? `${cancelAppointmentRouteFromBloomreach}?id=${encodeURIComponent(
    appointmentId
  )}&returnPath=${appointmentsRoute}&returnLabel=${encodeURIComponent(returnLabel)}` : void 0);
  const dateAndTime = appointment ? `${dateTime.formatLongDate(appointment?.startTime)} - ${dateTime.formatTime(
    appointment?.startTime
  )}` : "";
  const store = appointment?.store;
  const mapImageUrl = useGoogleMapsStaticMap({
    lat: store?.lat,
    lon: store?.lon,
    params: "&style=feature:poi|visibility:off"
  });
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    [store?.streetName.split(" "), store?.streetNumber, store?.town].join(" ").trim()
  )}`;
  const labels = {
    additionalInfo: getLabel("appointments.page.reschedule.confirm.additionalInformation"),
    addToCalendar: getLabel("appointments.confirmation.addToCalendar"),
    appointments: getLabel("account.menu.appointments"),
    breadcrumb: getLabel("account.appointment.detail.breadcrumb"),
    cancel: getLabel("account.appointment.cancel.title"),
    detailError: getLabel("account.appointment.detail.error"),
    getDirections: getLabel("appointments.confirmation.getDirections"),
    reschedule: getLabel("account.appointment.action.reschedule"),
    reservationInfo: getFormattedLabel("appointments.reservation.infoAlert.message", {
      parseHTML: true
    }),
    reservationTitle: getFormattedLabel("appointments.reservation.infoAlert.title", {
      parseHTML: true
    }),
    viewStoreDetails: getLabel("appointments.component.storeOverview.storeDetailLink"),
    when: getLabel("appointments.component.navigationContainer.step.when"),
    where: getLabel("appointments.component.navigationContainer.step.where"),
    who: getLabel("appointments.component.navigationContainer.step.who"),
    child: getLabel("appointments.component.appointmentOverview.child")
  };
  const handleOnReschedule = () => {
    appointment && dispatch(rescheduleAppointment(appointment));
  };
  const handleOpenStoreDetailModal = React.useCallback(() => setStoreDetailModalOpen(true), []);
  const handleCloseStoreDetailModal = React.useCallback(() => {
    storeButtonRef.current?.focus();
    setStoreDetailModalOpen(false);
  }, [storeButtonRef]);
  if (loading) {
    return /* @__PURE__ */ React.createElement(Spinner, { "aria-label": getLabel("common.loadingText") });
  }
  if (error || !appointment) {
    return /* @__PURE__ */ React.createElement(Alert, { variant: "error", className: "alert-m-default" }, labels.detailError);
  }
  const isOldAppointment = DateUtils.isDateBefore(appointment.endTime, /* @__PURE__ */ new Date());
  return /* @__PURE__ */ React.createElement(
    AppointmentDetail,
    {
      appointmentType: appointment.appointmentType,
      isVirtualAppointmentType: isVirtualAppointmentType(appointment.appointmentType),
      cancelLink: cancelPath,
      customerComment: appointment.customerComment,
      dateAndTime,
      directionsUrl: !isVirtualAppointmentType(appointment.appointmentType) ? directionsUrl : void 0,
      endTime: appointment.endTime,
      invitee: appointment.invitee,
      isCancellable: appointment.isCancellable,
      isReschedulable: appointment.isReschedulable,
      isReservation: appointment.isReservation,
      labels,
      mapImageUrl: !isVirtualAppointmentType(appointment.appointmentType) ? mapImageUrl : void 0,
      onReschedule: handleOnReschedule,
      onStoreDetailModalClose: handleCloseStoreDetailModal,
      onStoreDetailModalOpen: handleOpenStoreDetailModal,
      rescheduleLink: reschedulePath,
      startTime: appointment.startTime,
      storeDetailModalOpen,
      store: !isVirtualAppointmentType(appointment.appointmentType) ? store : void 0,
      child: appointment.child,
      isOldAppointment
    }
  );
};
export {
  AppointmentDetailContainer as default
};
//# sourceMappingURL=AppointmentDetailContainer.js.map
